import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getRecipe, updateRecipe, deleteRecipe, getIngredients,
  downloadGyartmanylap, downloadHaccp, downloadLabel, triggerPdfDownload, openPdfPreview
} from '../api/client'
import { ArrowLeft, FileText, ClipboardCheck, Tag, Plus, Trash2, Save, Eye, Download } from 'lucide-react'
import toast from 'react-hot-toast'

interface IngredientRow { ingredient_id?: number; component_recipe_id?: number; ingredient_name?: string; quantity_grams: number; order_index: number }

export default function RecipeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const recipeId = Number(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: recipe, isLoading } = useQuery({
    queryKey: ['recipe', recipeId],
    queryFn: () => getRecipe(recipeId).then(r => r.data),
  })

  const { data: allIngredients = [] } = useQuery({
    queryKey: ['ingredients'],
    queryFn: () => getIngredients().then(r => r.data),
  })

  const [searchQ, setSearchQ] = useState('')
  const [labelSize, setLabelSize] = useState('100x70')
  const [bestBefore, setBestBefore] = useState('')
  const [productionDate, setProductionDate] = useState('')
  const [labelMode, setLabelMode] = useState<'fogyasztható' | 'fagyasztva'>('fogyasztható')
  const [includeQr, setIncludeQr] = useState(false)
  const [labelLoading, setLabelLoading] = useState(false)
  const [gyLoading, setGyLoading] = useState(false)
  const [haccpLoading, setHaccpLoading] = useState(false)
  const [gyPreviewLoading, setGyPreviewLoading] = useState(false)
  const [haccpPreviewLoading, setHaccpPreviewLoading] = useState(false)
  const [labelPreviewLoading, setLabelPreviewLoading] = useState(false)

  const updateMut = useMutation({
    mutationFn: (data: object) => updateRecipe(recipeId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['recipe', recipeId] }); toast.success('Mentve') },
    onError: () => toast.error('Hiba mentéskor'),
  })

  const deleteMut = useMutation({
    mutationFn: () => deleteRecipe(recipeId),
    onSuccess: () => { toast.success('Recept törölve'); navigate('/receptek') },
  })

  const [rows, setRows] = useState<IngredientRow[]>([])
  const [rowsInit, setRowsInit] = useState(false)

  if (recipe && !rowsInit) {
    setRows(recipe.ingredients.map((i: any) => ({
      ingredient_id: i.ingredient_id || undefined,
      component_recipe_id: i.component_recipe_id || undefined,
      ingredient_name: i.ingredient_name,
      quantity_grams: i.quantity_grams,
      order_index: i.order_index,
    })))
    setRowsInit(true)
  }

  function addRow(ingId: number) {
    setRows(r => [...r, { ingredient_id: ingId, quantity_grams: 100, order_index: r.length }])
    setSearchQ('')
  }

  function removeRow(idx: number) {
    setRows(r => r.filter((_, i) => i !== idx))
  }

  function updateQty(idx: number, qty: number) {
    setRows(r => r.map((row, i) => i === idx ? { ...row, quantity_grams: qty } : row))
  }

  function save() {
    if (!recipe) return
    updateMut.mutate({
      name: recipe.name,
      category: recipe.category,
      batch_yield_grams: recipe.batch_yield_grams,
      shelf_life_days: recipe.shelf_life_days,
      storage_temp_min: recipe.storage_temp_min,
      storage_temp_max: recipe.storage_temp_max,
      storage_notes: recipe.storage_notes,
      net_weight_grams: recipe.net_weight_grams,
      description: recipe.description,
      version: recipe.version,
      approved_by: recipe.approved_by,
      ingredients: rows.map(({ ingredient_name, ...r }) => r),
    })
  }

  async function dlGyartmanylap() {
    setGyLoading(true)
    try {
      const res = await downloadGyartmanylap(recipeId)
      triggerPdfDownload(res.data, `gyartmanylap_${recipe?.name}.pdf`)
    } catch { toast.error('PDF generálás sikertelen') }
    finally { setGyLoading(false) }
  }

  async function previewGyartmanylap() {
    setGyPreviewLoading(true)
    try {
      const res = await downloadGyartmanylap(recipeId)
      openPdfPreview(res.data)
    } catch { toast.error('PDF generálás sikertelen') }
    finally { setGyPreviewLoading(false) }
  }

  async function dlHaccp() {
    setHaccpLoading(true)
    try {
      const res = await downloadHaccp(recipeId)
      triggerPdfDownload(res.data, `haccp_${recipe?.name}.pdf`)
    } catch { toast.error('PDF generálás sikertelen') }
    finally { setHaccpLoading(false) }
  }

  async function previewHaccp() {
    setHaccpPreviewLoading(true)
    try {
      const res = await downloadHaccp(recipeId)
      openPdfPreview(res.data)
    } catch { toast.error('PDF generálás sikertelen') }
    finally { setHaccpPreviewLoading(false) }
  }

  async function dlLabel() {
    setLabelLoading(true)
    try {
      const res = await downloadLabel(recipeId, {
        recipe_id: recipeId,
        label_size: labelSize,
        production_date: productionDate || null,
        label_type: labelMode,
        include_qr: includeQr,
      })
      triggerPdfDownload(res.data, `cimke_${recipe?.name}.pdf`)
    } catch { toast.error('PDF generálás sikertelen') }
    finally { setLabelLoading(false) }
  }

  async function previewLabel() {
    setLabelPreviewLoading(true)
    try {
      const res = await downloadLabel(recipeId, {
        recipe_id: recipeId,
        label_size: labelSize,
        production_date: productionDate || null,
        label_type: labelMode,
        include_qr: includeQr,
      })
      openPdfPreview(res.data)
    } catch { toast.error('PDF generálás sikertelen') }
    finally { setLabelPreviewLoading(false) }
  }

  const filteredIng = allIngredients.filter((i: any) =>
    i.name.toLowerCase().includes(searchQ.toLowerCase()) &&
    !rows.find(r => r.ingredient_id === i.id)
  ).slice(0, 8)

  if (isLoading) return <div className="p-8 text-gray-400">Betöltés...</div>
  if (!recipe) return <div className="p-8 text-red-500">Recept nem található</div>

  const n = recipe.nutrition

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <button onClick={() => navigate('/receptek')}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-5">
        <ArrowLeft className="w-4 h-4" /> Vissza
      </button>

      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 truncate">{recipe.name}</h1>
          <p className="text-sm text-gray-500 mt-1">{recipe.category} · v{recipe.version}</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button className="btn-primary flex items-center gap-2" onClick={save} disabled={updateMut.isPending}>
            <Save className="w-4 h-4" />
            <span className="hidden sm:inline">{updateMut.isPending ? 'Mentés...' : 'Mentés'}</span>
          </button>
          <button className="btn-danger flex items-center gap-2"
            onClick={() => { if (confirm('Törli a receptet?')) deleteMut.mutate() }}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Ingredients */}
        <div className="col-span-1 lg:col-span-2 space-y-4">
          <div className="card p-5">
            <h2 className="font-semibold text-gray-800 mb-4">Alapanyagok</h2>
            <div className="space-y-2 mb-4">
              {rows.map((row, idx) => {
                const ing = row.ingredient_id ? (allIngredients.find((i: any) => i.id === row.ingredient_id) as any) : null
                const isComponentRecipe = !!row.component_recipe_id
                return (
                  <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <span className="flex-1 text-sm font-medium text-gray-800">
                      {ing?.name ?? row.ingredient_name ?? `#${row.ingredient_id}`}
                      {isComponentRecipe && (
                        <span className="ml-1.5 text-[10px] uppercase tracking-wide text-brand-500 bg-brand-50 border border-brand-200 rounded px-1 py-0.5">alaprecept</span>
                      )}
                    </span>
                    {ing?.allergens?.length > 0 && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                        {ing.allergens.join(', ')}
                      </span>
                    )}
                    <input
                      type="number"
                      className="input w-24 text-right text-sm py-1"
                      value={row.quantity_grams}
                      onChange={e => updateQty(idx, +e.target.value)}
                    />
                    <span className="text-xs text-gray-400 w-4">g</span>
                    <button onClick={() => removeRow(idx)} className="text-red-400 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )
              })}
              {rows.length === 0 && (
                <p className="text-sm text-gray-400 py-2">Adj hozzá alapanyagokat az alábbi kereső segítségével.</p>
              )}
            </div>
            <div className="relative">
              <input
                className="input pr-8"
                placeholder="Alapanyag keresése és hozzáadása..."
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
              />
              {searchQ && filteredIng.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-10 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-auto">
                  {filteredIng.map((i: any) => (
                    <button
                      key={i.id}
                      onClick={() => addRow(i.id)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-brand-50 text-left"
                    >
                      <span>{i.name}</span>
                      <div className="flex items-center gap-2">
                        {i.allergens?.length > 0 && (
                          <span className="text-xs text-amber-600">{i.allergens.join(', ')}</span>
                        )}
                        <span className="text-xs text-gray-400">{i.energy_kcal} kcal</span>
                        <Plus className="w-3 h-3 text-brand-500" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Nutrition table */}
          {n && (
            <div className="card p-5">
              <h2 className="font-semibold text-gray-800 mb-3">
                Tápértéktáblázat — 100 g késztermékre
                <span className="ml-2 text-xs font-normal text-gray-400">(EU 1169/2011)</span>
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-brand-500 text-white rounded-lg p-3 text-center col-span-2">
                  <div className="text-2xl font-bold">{Math.round(n.energy_kcal)} kcal</div>
                  <div className="text-sm opacity-80">{Math.round(n.energy_kj)} kJ</div>
                </div>
                {[
                  ['Zsír', n.fat, 'g'],
                  ['— telített zsírsav', n.saturated_fat, 'g'],
                  ['Szénhidrát', n.carbs, 'g'],
                  ['— cukrok', n.sugars, 'g'],
                  ['Élelmi rost', n.fiber, 'g'],
                  ['Fehérje', n.protein, 'g'],
                  ['Só', n.salt, 'g'],
                ].map(([label, val, unit]) => (
                  <div key={String(label)} className="flex justify-between text-sm py-1 border-b border-gray-100">
                    <span className="text-gray-600">{label}</span>
                    <span className="font-medium">{val} {unit}</span>
                  </div>
                ))}
              </div>
              {n.allergens.length > 0 && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-amber-800">
                    Allergének: {n.allergens.join(', ')}
                  </p>
                </div>
              )}
              <div className="mt-3">
                <p className="text-xs text-gray-500 font-medium mb-1">Összetevők (csökkenő sorrendben):</p>
                <p className="text-xs text-gray-600 leading-relaxed">{n.ingredient_list}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right: Documents + Settings */}
        <div className="space-y-4">
          {/* Document generation */}
          <div className="card p-5">
            <h2 className="font-semibold text-gray-800 mb-4">Dokumentumok</h2>
            <div className="space-y-2">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-blue-900">Gyártmánylap</div>
                    <div className="text-xs text-blue-600">3 oldalas termékspecifikáció</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded border border-blue-300 bg-white hover:bg-blue-50 text-blue-700 transition-colors"
                    onClick={previewGyartmanylap}
                    disabled={gyPreviewLoading}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    {gyPreviewLoading ? '...' : 'Előnézet'}
                  </button>
                  <button
                    className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    onClick={dlGyartmanylap}
                    disabled={gyLoading}
                  >
                    <Download className="w-3.5 h-3.5" />
                    {gyLoading ? '...' : 'Letöltés'}
                  </button>
                </div>
              </div>

              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <ClipboardCheck className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-red-900">HACCP Dokumentáció</div>
                    <div className="text-xs text-red-600">Folyamatlap + CCP-k</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded border border-red-300 bg-white hover:bg-red-50 text-red-700 transition-colors"
                    onClick={previewHaccp}
                    disabled={haccpPreviewLoading}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    {haccpPreviewLoading ? '...' : 'Előnézet'}
                  </button>
                  <button
                    className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
                    onClick={dlHaccp}
                    disabled={haccpLoading}
                  >
                    <Download className="w-3.5 h-3.5" />
                    {haccpLoading ? '...' : 'Letöltés'}
                  </button>
                </div>
              </div>

              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-900">Etikett / Címke</span>
                </div>
                <div className="space-y-2">
                  <select
                    className="input text-xs py-1.5"
                    value={labelSize}
                    onChange={e => setLabelSize(e.target.value)}
                  >
                    <option value="100x70">100×70 mm (standard)</option>
                    <option value="100x60">100×60 mm</option>
                    <option value="60x50">60×50 mm (közepes)</option>
                    <option value="60x40">60×40 mm</option>
                    <option value="50x30">50×30 mm (kis)</option>
                    <option value="A4">A4 (9 db / oldal)</option>
                    <option value="62_kicsi">Brother QL – 62 mm tekercs, kicsi (apró betű, legrövidebb címke)</option>
                    <option value="62_tekercs">Brother QL – 62 mm tekercs, nagy (jól olvasható)</option>
                  </select>
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input type="radio" name="labelMode" className="accent-amber-600" checked={labelMode === 'fogyasztható'} onChange={() => setLabelMode('fogyasztható')} />
                      <span className="text-xs text-amber-800">
                        Fogyasztható: gyártástól számított {recipe?.shelf_life_days || '?'} nap · {recipe?.storage_temp || `${recipe?.storage_temp_min ?? 0}–${recipe?.storage_temp_max ?? 6}°C`}
                      </span>
                    </label>
                    {!!recipe?.frozen_shelf_life_months && (
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input type="radio" name="labelMode" className="accent-amber-600" checked={labelMode === 'fagyasztva'} onChange={() => setLabelMode('fagyasztva')} />
                        <span className="text-xs text-amber-800">
                          Minőségét megőrzi: gyártástól számított {recipe.frozen_shelf_life_months} hónap · {recipe?.frozen_storage_temp || '-18'}°C
                        </span>
                      </label>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-amber-700 mb-0.5 block">Gyártva (üresen hagyható)</label>
                    <input
                      type="date"
                      className="input text-xs py-1.5"
                      value={productionDate}
                      onChange={e => setProductionDate(e.target.value)}
                    />
                    <p className="text-xs text-amber-500 mt-0.5">Ha üresen hagyod, a cimkén üres marad — kézzel kitölthető.</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input type="checkbox" className="accent-amber-600" checked={includeQr} onChange={e => setIncludeQr(e.target.checked)} />
                    <span className="text-xs text-amber-800">
                      Nyomonkövetési QR-kód a címke sarkába (beszállítói tétel adatok)
                    </span>
                  </label>
                  <div className="flex gap-2">
                    <button
                      className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded border border-amber-300 bg-white hover:bg-amber-50 text-amber-700 transition-colors"
                      onClick={previewLabel}
                      disabled={labelPreviewLoading}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      {labelPreviewLoading ? '...' : 'Előnézet'}
                    </button>
                    <button
                      className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                      onClick={dlLabel}
                      disabled={labelLoading}
                    >
                      <Download className="w-3.5 h-3.5" />
                      {labelLoading ? '...' : 'Letöltés'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recipe meta */}
          <div className="card p-5">
            <h2 className="font-semibold text-gray-800 mb-3">Recept adatok</h2>
            <div className="space-y-3 text-sm">
              <div>
                <label className="label">Köteg hozam (g)</label>
                <input className="input" type="number" defaultValue={recipe.batch_yield_grams}
                  onBlur={e => { recipe.batch_yield_grams = +e.target.value }} />
              </div>
              <div>
                <label className="label">Kiszerelés (g)</label>
                <input className="input" type="number" defaultValue={recipe.net_weight_grams || ''}
                  onBlur={e => { recipe.net_weight_grams = +e.target.value || null }} />
                <p className="text-xs text-gray-400 mt-1">Ha üresen hagyod, a rendszer automatikusan kiszámolja az összetevők súlyából.</p>
              </div>
              <div>
                <label className="label">Fogyasztható (nap)</label>
                <input className="input" type="number" defaultValue={recipe.shelf_life_days}
                  onBlur={e => { recipe.shelf_life_days = +e.target.value }} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label">Tárolásmód (min °C)</label>
                  <input className="input" type="number" defaultValue={recipe.storage_temp_min}
                    onBlur={e => { recipe.storage_temp_min = +e.target.value }} />
                </div>
                <div>
                  <label className="label">Tárolásmód (max °C)</label>
                  <input className="input" type="number" defaultValue={recipe.storage_temp_max}
                    onBlur={e => { recipe.storage_temp_max = +e.target.value }} />
                </div>
              </div>
              <div>
                <label className="label">Tárolási megjegyzés</label>
                <input className="input" defaultValue={recipe.storage_notes || ''}
                  onBlur={e => { recipe.storage_notes = e.target.value }} />
              </div>
              <div>
                <label className="label">Jóváhagyta</label>
                <input className="input" defaultValue={recipe.approved_by || ''}
                  onBlur={e => { recipe.approved_by = e.target.value }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
